/*
    Author: draplater
    Create Time: 18-3-21
    Project: verbnet2017
*/

import * as React from 'react';
import * as _ from "lodash";
import {CurrentLexiconData, LexiconPage, LexiconProps, SentenceGroup, SentenceViewer} from "./LexiconPage";
import {HiddenBlock, renderPath} from "./utils";
import {FrameMeta, FrameInfo} from "../problem-domain/frame";
import {FrameInfoWire, VerbWire} from "../bridge";
import {connect} from "react-redux";
import {GlobalState} from "../reduxSetting";
import {MenuManagerWrapperRedux} from "../Menu";
import {Panel, Table} from "react-bootstrap";
import * as DocumentTitle from 'react-document-title';
import {Verb} from "../problem-domain/verb";
import {groupByPattern} from "../tagUtils";
import {Link} from "react-router-dom";
import {getLexiconLink} from "./route-utils";
import {colors} from "../utils";


function renderPercentage(groupName, groups, lexicons): JSX.Element {
    return <span> {"("} {
        groups.map((g, idx) => {
            const percentage = g.get(groupName)!.length / lexicons[idx].verb.sentences.length * 100;
            return (<span
                className="compare-percentage"> {percentage.toFixed(2) + "% of " + lexicons[idx].verb.name} </span>);
        })
    } {")"} </span>;
}

function renderLexiconComparison(frameIndex: FrameMeta, lexicons: Array<CurrentLexiconData>) {
    const lexiconNames = lexicons.map(x => x.path.slice(-1)[0]);
    const titlePart = lexicons.map(
        x => <span className="lexicon-title-compare"><Link to={getLexiconLink(x.path)}>
            {x.path.slice(-1)[0]}</Link></span>);

    // sentences to groups
    const groups = lexicons.map(x => groupByPattern(x.verb.sentences));
    const groupNames = groups.map(x => Array.from(x.keys()));
    const commonGroups = _.intersection(...groupNames);
    const groups_only = [_.without(groupNames[0], ...groupNames[1]),
        _.without(groupNames[1], ...groupNames[0])];

    // build color map
    const parentFrames = Array.from(new Set(lexicons.map(x => x.frameChain.slice(-1)[0])));
    const frameNames = parentFrames.map(x => x.name);

    const colorMapFunc = (tag: string) => {
        /* tag => color */
        if (frameNames.indexOf(tag) >= 0) {
            return colors[1];
        } else if (tag.startsWith("*")) {
            return colors[2];
        } else {
            return colors[0];
        }
    };

    return (
        <div className="lexicon-info">
            <h1> Compare: {titlePart} </h1>
            <div>
                <Table striped bordered condensed hover>
                    <thead>
                    <tr>
                        <th/>
                        {lexiconNames.map(x => <th>{x}</th>)}
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td> Frequency in Gigaword</td>
                        {lexicons.map(x => <td>{x.verb.frequency}</td>)}
                    </tr>
                    </tbody>
                </Table>
            </div>
            <h2> Common Patterns </h2>
            <HiddenBlock>
                {commonGroups.length ? commonGroups.map(x =>
                    <SentenceGroup pattern={x.split(",")}
                                   sentences={_.union(...groups.map(g => g.get(x)))}
                                   colorMap={colorMapFunc}
                                   customTitle={renderPercentage(x, groups, lexicons)}
                                   expanded={true}
                                   hideCount/>) : ["(none)"]
                }
            </HiddenBlock>
            {
                _.range(lexicons.length).map(i => <div>
                        <h2> {lexiconNames[i]}-specific patterns</h2>
                        <HiddenBlock>
                            {groups_only[i].length ?
                                groups_only[i].map(x =>
                                    <SentenceGroup pattern={x.split(",")}
                                                   sentences={groups[i].get(x)!}
                                                   expanded={false}
                                                   percentage={
                                                       groups[i].get(x)!.length / lexicons[i].verb.sentences.length}
                                                   colorMap={colorMapFunc}/>)
                                :
                                [`${lexiconNames[i]} has no unique pattern.`]
                            }
                        </HiddenBlock>
                    </div>
                )
            }
        </div>
    )
}

interface ComparisonManagedProps {
    frameIndex?: FrameMeta,
    verbPaths: string[][]
}

export class ComparisonManaged extends React.Component<ComparisonManagedProps,
    { data?: CurrentLexiconData[], pending: boolean }> {
    constructor(props: ComparisonManagedProps) {
        super(props);
        this.state = {pending: false};
    }

    componentDidMount() {
        this.getLexicon(this.props);
    }

    componentWillReceiveProps(newProps) {
        this.getLexicon(newProps);
    }

    componentWillUnmount() {
        this.setState({data: undefined});
    }

    static async getVerbInfo(frameIndex: FrameMeta,
                             verbPath: string[]): Promise<CurrentLexiconData> {
        const verbInfoPromise = VerbWire.get(verbPath);
        let parentFrameInfo: FrameInfo | undefined = undefined;
        // only parent frame is needed now
        // but we use array there in case we want to use grand parent frame in future
        const parentFrameMeta = frameIndex.find(verbPath.slice(0, -1));
        if (parentFrameMeta && parentFrameMeta.def) {
            parentFrameInfo = await FrameInfoWire.get(verbPath.slice(0, -1),
                parentFrameMeta.def);
        }
        const verbInfo = await verbInfoPromise;
        const frameChain = parentFrameInfo ? [parentFrameInfo] : [];
        return {
            verb: verbInfo, frameChain, path: verbPath
        };
    }

    async getLexicon(props: ComparisonManagedProps) {
        console.log(props);
        if (!props.frameIndex) {
            return;
        }

        this.setState({pending: true});
        this.setState({
            data: await Promise.all(props.verbPaths.map(async x =>
                ComparisonManaged.getVerbInfo(props.frameIndex!, x))),
            pending: false
        });
    }

    render() {
        if (this.props.frameIndex) {
            if (this.state.data) {
                return <div className="main-panel">
                    {(this.state.pending) &&
                    <div style={{
                        position: "absolute",
                        width: "100%",
                        textAlign: "center",
                    }}><span>Loading Frame...</span></div>}
                    <div style={{opacity: this.state.pending ? 0.4 : 1}}>
                        {renderLexiconComparison(this.props.frameIndex, this.state.data)}
                    </div>
                </div>
            } else {
                return <div className="main-panel"> Loading Verb...</div>;
            }
        } else {
            return <div className="main-panel"> Loading Frame Index...</div>;
        }
    }
}

export const ComparisonManagedRedux = connect(
    (state: GlobalState, ownProps: { verbPaths: string[][] }): any => {
        return {
            frameIndex: state.index.frames.length ? FrameMeta.wrapTop(state.index.frames) : undefined,
            verbPaths: ownProps.verbPaths
        };
    },
    dispatch => ({})
)(ComparisonManaged as any);

export function renderComparisonWithRoute({location}) {
    window.scrollTo(0, 0);
    const verbPaths: string[][] = location.pathname.replace("/comparison", "").split(":")
        .map(x => x.substr(1).split("/"));
    const titlePart = verbPaths.map(x => x.slice(-1)[0]).join(" & ");

    return <DocumentTitle title={`${titlePart} - Comparison - Mandarin VerbNet`}>
        <MenuManagerWrapperRedux>
            <Panel><ComparisonManagedRedux verbPaths={verbPaths}/></Panel>
        </MenuManagerWrapperRedux>
    </DocumentTitle>;
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/compare-lexicons.tsx